import { useEffect, useState } from "react";
import { Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { ShareBar } from "@/components/ShareBar";
import { TOPIC_LABELS } from "@/components/NewsList";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/** The dated news card: five topic pictures redrawn daily plus today's headlines. */
export const NewsBoardCard = ({ canRefresh = false }) => {
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/news/card").then(({ data }) => setCard(data)).catch(() => setCard(null));
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setBusy(true);
    try { await api.post("/news/refresh"); await load(); } catch {} finally { setBusy(false); }
  };

  const pictures = Object.entries(card?.pictures || {});
  if (!card || (!pictures.length && !(card.headlines || []).length)) return null;

  return (
    <section className="border border-[#002FA7] bg-white p-4 sm:p-5" data-testid="news-board-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="overline text-[#002FA7]">
          Today&apos;s industry news{card.day ? ` · ${card.day}` : ""}
        </p>
        <div className="flex items-center gap-3">
          {card.auto_posted && (
            <span data-testid="news-card-posted" className="overline text-[0.6rem] text-[#0B5C2E]">Posted to Facebook</span>
          )}
          {canRefresh && (
            <button type="button" data-testid="news-card-refresh" onClick={refresh} disabled={busy}
              className="tap-safe text-xs font-semibold text-[#002FA7]">
              <Sparkle size={13} className="mr-1 inline align-[-2px]" />
              {busy ? "Redrawing…" : "Redraw the pictures"}
            </button>
          )}
        </div>
      </div>

      {pictures.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="news-card-pictures">
          {pictures.map(([topic, url]) => (
            <figure key={topic} className="border border-border" data-testid={`news-picture-${topic}`}>
              <img src={`${BACKEND}${url}`} alt={TOPIC_LABELS[topic] || topic} loading="lazy"
                className="h-24 w-full object-cover" />
              <figcaption className="px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-slate-500">
                {TOPIC_LABELS[topic] || topic}
              </figcaption>
            </figure>
          ))}
        </div>
      )}

      {(card.headlines || []).length > 0 && (
        <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="news-card-headlines">
          {card.headlines.map((h, i) => (
            <li key={i}>
              <a href={h.url} target="_blank" rel="noopener noreferrer"
                data-testid={`news-card-headline-${i}`} className="hover:text-[#002FA7]">· {h.title}</a>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3">
        <ShareBar
          url={card.share_token ? `${window.location.origin}/p/${card.share_token}` : `${window.location.origin}/news`}
          text={`Today's industry news — ${(card.headlines || []).map((h) => h.title).slice(0, 2).join(" · ")}`}
          testid="news-card-share" instagram compact />
      </div>
    </section>
  );
};
